import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Upload as UploadIcon, Building2, FileWarning, CheckCircle2,
  ClipboardCheck, TrendingUp, Building, Package, MapPin, ArrowRight,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import DonutPanel from '../components/DonutPanel';
import MapPanel from '../components/MapPanel';
import TopFilterBar from '../components/TopFilterBar';
import ActiveFilterChips from '../components/ActiveFilterChips';
import { TrendPanel } from '../components/ChartsRow';
import RegionDrawer from '../components/RegionDrawer';
import { getDashboardData, getUploadStatus } from '../api';
import { getSupplyData, getSupplyRows } from '../api_extra';

const DEFAULT_FILTERS = {
  category: [], status: [], priority: [], region: [], governorate: [],
  education_office: [], school_id: 'all', contractor: [], date_from: null, date_to: null,
};
const STORAGE_KEY = 'tbc_dashboard_filters_v2';
const readF = () => { try { const r = localStorage.getItem(STORAGE_KEY); return r ? { ...DEFAULT_FILTERS, ...JSON.parse(r) } : DEFAULT_FILTERS; } catch { return DEFAULT_FILTERS; } };
const saveF = (f) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(f)); } catch {} };

function eqDeep(a, b) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const va = a[k], vb = b[k];
    if (Array.isArray(va) && Array.isArray(vb)) {
      if (va.length !== vb.length || va.some((x, i) => x !== vb[i])) return false;
    } else if (va !== vb) return false;
  }
  return true;
}
function countActive(f) {
  return Object.entries(f).reduce((n, [k, v]) => {
    if (k === 'period') return n;
    if (Array.isArray(v)) return n + v.length;
    if (v && v !== 'all') return n + 1;
    return n;
  }, 0);
}

function SectionHead({ eyebrow, title, subtitle, right, testid }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4 md:mb-5" data-testid={testid}>
      <div>
        <div className="section-eyebrow">{eyebrow}</div>
        <h2 className="section-title mt-1.5">{title}</h2>
        {subtitle && <div className="text-slate-400 text-[12.5px] mt-1">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

function KpiCard({ label, value, unit, icon: Icon, tint = '#3B82F6', onClick, testid, hero }) {
  const cls = hero ? 'panel-hero panel-hoverable px-5 py-5' : 'panel panel-hoverable px-5 py-5';
  return (
    <button onClick={onClick} className={`${cls} flex flex-col justify-between text-right min-w-0`}
      data-testid={testid} style={{ minHeight: 140 }}>
      <div className="flex items-start justify-between gap-2 relative z-10">
        <div className="text-slate-300 text-[12.5px] font-semibold leading-tight truncate">{label}</div>
        {Icon && (
          <div className="icon-chip shrink-0" style={{ background: `${tint}22`, color: tint, border: `1px solid ${tint}44` }}>
            <Icon size={16} strokeWidth={1.75} />
          </div>
        )}
      </div>
      <div className="mt-3 relative z-10">
        <div className={`kpi-number text-[30px] leading-none ${hero ? 'text-gradient-blue' : 'text-white'}`}>{value}</div>
        {unit && <div className="text-slate-400 text-[11.5px] mt-1.5 truncate">{unit}</div>}
      </div>
    </button>
  );
}

/* Small "Status Summary" tile for the second row */
function StatusSummaryPanel({ kpis }) {
  const items = [
    { label: 'البلاغات المفتوحة', v: kpis.openTickets,      color: '#F59E0B' },
    { label: 'البلاغات المغلقة',  v: kpis.completedTickets, color: '#22C55E' },
    { label: 'إجمالي البلاغات',   v: kpis.registeredTickets,color: '#3B82F6' },
    { label: 'نسبة الإنجاز',      v: (kpis.completionRate ?? 0) + '%', color: '#22D3EE' },
    { label: 'متوسط الإغلاق',     v: (kpis.avgCloseDays ?? 0) + ' يوم', color: '#A855F7' },
    { label: 'المدارس المتأخرة',  v: kpis.delayedSchools,    color: '#EF4444' },
  ];
  return (
    <div className="panel p-5">
      <div className="panel-header">
        <div><div className="panel-title">ملخص الحالة</div><div className="panel-subtitle">Status Summary</div></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((it, i) => (
          <div key={i} className="border hair rounded-lg p-3 flex items-center justify-between gap-2 bg-white/[0.015]">
            <span className="text-slate-400 text-[11.5px] font-medium truncate">{it.label}</span>
            <span className="tabular-nums font-bold text-[15px]" style={{ color: it.color }}>
              {typeof it.v === 'number' ? it.v.toLocaleString('en-US') : (it.v ?? '—')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Latest Tickets table — driven by data.alerts (recent tickets) ─── */
function LatestTicketsTable({ alerts, onSelectSchool }) {
  const rows = (alerts || []).slice(0, 10);
  const statusPill = (s) => {
    const t = (s || '').toString();
    if (/(مغلق|مكتمل|closed|complete|resolved|done)/i.test(t)) return 'pill pill-green';
    if (/(مفتوح|جديد|قيد|open|new|pending|in progress)/i.test(t)) return 'pill pill-amber';
    return 'pill pill-blue';
  };
  const priorityPill = (p) => {
    const s = (p || '').toString();
    if (/(حرج|critical|urgent|عالي|high)/i.test(s)) return 'pill pill-red';
    if (/(متوسط|medium|normal)/i.test(s)) return 'pill pill-amber';
    return 'pill pill-blue';
  };
  return (
    <div className="panel p-5" data-testid="latest-tickets-panel">
      <div className="panel-header">
        <div><div className="panel-title">أحدث البلاغات</div><div className="panel-subtitle">Latest Tickets · آخر 10 بلاغات · اضغط على أي بلاغ لفتح ملف المدرسة</div></div>
      </div>
      {rows.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-[13px]">لا توجد بلاغات مطابقة</div>
      ) : (
        <div className="overflow-x-auto border hair rounded-lg">
          <table className="w-full text-[12.5px]">
            <thead className="bg-white/[0.03]">
              <tr>
                <th className="p-2.5 text-slate-300 text-right font-semibold">رقم البلاغ</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">المدرسة</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">التصنيف</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">الحالة</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">الأولوية</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => {
                const clickable = !!t.school_id;
                return (
                  <tr key={i}
                      data-testid={`latest-ticket-row-${i}`}
                      className={`border-t border-white/[0.03] transition-colors ${clickable ? 'hover:bg-blue-500/[0.06] cursor-pointer' : 'opacity-70'}`}
                      title={clickable ? 'اضغط لفتح ملف المدرسة' : 'هذا البلاغ غير مرتبط بمدرسة'}
                      onClick={() => clickable && onSelectSchool && onSelectSchool(t.school_id)}>
                    <td className="p-2.5 text-slate-100 font-semibold whitespace-nowrap">
                      {t.ticket_id || t.id || '—'}
                      {!clickable && <span className="mr-2 pill pill-amber text-[10px]">غير مرتبط</span>}
                    </td>
                    <td className="p-2.5 truncate max-w-[220px]" title={t.school_name}>
                      {clickable
                        ? <span className="text-blue-300 font-semibold hover:text-blue-200">{t.school_name || t.school_id || '—'}</span>
                        : <span className="text-slate-500 italic">هذا البلاغ غير مرتبط بمدرسة</span>}
                    </td>
                    <td className="p-2.5 text-slate-300 truncate max-w-[160px]">{t.category || '—'}</td>
                    <td className="p-2.5"><span className={statusPill(t.status)}>{t.status || '—'}</span></td>
                    <td className="p-2.5"><span className={priorityPill(t.priority)}>{t.priority || '—'}</span></td>
                    <td className="p-2.5 text-slate-500 whitespace-nowrap">
                      {t.created_date ? new Date(t.created_date).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Latest Supply table — fetches most recent supply rows ─────── */
function LatestSupplyTable() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    getSupplyRows({ page: 1, page_size: 10, sort: 'delivery_date', sort_dir: 'desc' })
      .then((res) => { if (alive) setRows(res.rows || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);
  const statusPill = (s) => s === 'delivered' ? 'pill pill-green' : s === 'delayed' ? 'pill pill-red' : 'pill pill-amber';
  const statusLbl = { delivered: 'تم التوريد', pending: 'قيد التوريد', delayed: 'متأخر' };
  return (
    <div className="panel p-5" data-testid="latest-supply-panel">
      <div className="panel-header">
        <div><div className="panel-title">أحدث التوريدات</div><div className="panel-subtitle">Latest Supply · آخر 10 عمليات</div></div>
      </div>
      {rows === null ? (
        <div className="py-8 flex items-center justify-center gap-2 text-slate-400 text-[13px]">
          <Loader2 size={16} className="animate-spin" /> جاري التحميل...
        </div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-[13px]">لا توجد بيانات توريد بعد</div>
      ) : (
        <div className="overflow-x-auto border hair rounded-lg">
          <table className="w-full text-[12.5px]">
            <thead className="bg-white/[0.03]">
              <tr>
                <th className="p-2.5 text-slate-300 text-right font-semibold">المدرسة</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">المورد</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">الصنف</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">الكمية</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">الحالة</th>
                <th className="p-2.5 text-slate-300 text-right font-semibold">تاريخ التوريد</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="p-2.5 text-slate-100 truncate max-w-[220px]" title={r.school_name}>{r.school_name || r.school_id || '—'}</td>
                  <td className="p-2.5 text-slate-300 truncate max-w-[180px]" title={r.company}>{r.company || '—'}</td>
                  <td className="p-2.5 text-slate-300 truncate max-w-[160px]" title={r.item}>{r.item || '—'}</td>
                  <td className="p-2.5 text-slate-300 tabular-nums">{(r.quantity ?? 0).toLocaleString('en-US')}</td>
                  <td className="p-2.5"><span className={statusPill(r.status)}>{statusLbl[r.status] || r.status || '—'}</span></td>
                  <td className="p-2.5 text-slate-500 whitespace-nowrap">
                    {r.delivery_date ? new Date(r.delivery_date).toLocaleDateString('en-GB') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [supply, setSupply] = useState(null);
  const [statusModules, setStatusModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(readF());
  const [applied, setApplied] = useState(readF());
  const [drawer, setDrawer] = useState({ open: false, key: null, value: null });

  const fetchAll = useCallback(async (f) => {
    setLoading(true); setError(null);
    try {
      const [d, s, st] = await Promise.all([
        getDashboardData(f),
        getSupplyData({}).catch(() => null),
        getUploadStatus().catch(() => ({})),
      ]);
      setData(d); setSupply(s); setStatusModules(st || {});
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(applied); saveF(applied); }, [fetchAll, applied]);

  const applyFilters = () => setApplied(filters);
  const resetFilters = () => { setFilters(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); };
  const removeKey = (k) => { const c = { ...applied, [k]: DEFAULT_FILTERS[k] }; setFilters(c); setApplied(c); };
  const removeValue = (k, v) => {
    const cur = applied[k];
    if (!Array.isArray(cur)) return removeKey(k);
    const next = { ...applied, [k]: cur.filter((x) => x !== v) };
    setFilters(next); setApplied(next);
  };
  const clickCategory = (cat) => {
    if (!cat) return;
    const cur = Array.isArray(applied.category) ? applied.category : [];
    const nextCat = cur.includes(cat) ? cur.filter((x) => x !== cat) : [...cur, cat];
    const next = { ...applied, category: nextCat };
    setFilters(next); setApplied(next);
  };

  const dirty = !eqDeep(filters, applied);
  const empty = data && !data.hasData;
  const activeCount = useMemo(() => countActive(applied), [applied]);
  const activeCategory = Array.isArray(applied.category) && applied.category.length === 1 ? applied.category[0] : null;

  const kpis = data?.kpis || {};
  const supKpis = supply?.kpis || {};
  const modCounts = statusModules?.extra_modules || {};

  const openSchool = (id) => id && nav(`/school/${encodeURIComponent(id)}`);

  return (
    <div dir="rtl" className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 relative">
        <div className="mx-auto max-w-[1440px] px-6 md:px-8 py-6 md:py-8 space-y-7 md:space-y-9">

          <Header onRefresh={() => fetchAll(applied)} />

          {loading && !data && (
            <div className="glass p-16 flex items-center justify-center gap-3 text-slate-300 fade-in">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-[13.5px]">جاري تحميل البيانات...</span>
            </div>
          )}
          {error && <div className="panel p-4 border-red-500/30 text-red-300 text-sm">خطأ: {error}</div>}

          {empty && (
            <div className="glass-hero p-14 flex flex-col items-center justify-center gap-4 text-center fade-in max-w-[900px] mx-auto">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center relative z-10"
                   style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.30) 0%, rgba(14,165,233,0.14) 100%)',
                            border: '1px solid rgba(59,130,246,0.40)', boxShadow: '0 12px 40px -14px rgba(59,130,246,0.55)' }}>
                <UploadIcon size={30} strokeWidth={1.6} className="text-[#60A5FA]" />
              </div>
              <div className="text-white text-[24px] font-extrabold tracking-tight relative z-10">لا توجد بيانات بعد</div>
              <div className="text-slate-300 text-[13px] max-w-md leading-relaxed relative z-10">
                ارفع ملف Excel للمدارس والبلاغات من مركز الاستيراد لبدء استعراض لوحة القيادة.
              </div>
              <button onClick={() => nav('/imports')} className="btn btn-primary relative z-10" data-testid="upload-cta">
                <UploadIcon size={14} strokeWidth={1.8} />
                <span>اذهب إلى مركز الاستيراد</span>
              </button>
            </div>
          )}

          {data && !empty && (
            <>
              {/* Filters */}
              <section className="space-y-3 fade-in">
                <TopFilterBar options={data.filterOptions} value={filters} onChange={setFilters}
                  onApply={applyFilters} onReset={resetFilters} dirty={dirty} loading={loading} activeCount={activeCount} />
                <ActiveFilterChips filters={applied} options={data.filterOptions}
                  onRemoveValue={removeValue} onRemoveKey={removeKey} onClearAll={resetFilters} />
              </section>

              {/* ── 1st row: 8 KPI cards ──────────────────────────────── */}
              <section className="fade-in">
                <SectionHead
                  eyebrow="نظرة عامة"
                  title="المؤشرات الرئيسية"
                  subtitle="لقطة لحظية عبر كل الوحدات"
                  testid="section-kpis"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                  <KpiCard hero label="إجمالي المدارس"       value={(kpis.totalSchools ?? 0).toLocaleString('en-US')}
                    unit="مدرسة" icon={Building2} tint="#3B82F6" onClick={() => nav('/schools')} testid="kpi-schools" />
                  <KpiCard hero label="نسبة الإنجاز"          value={`${kpis.completionRate ?? 0}%`}
                    unit="من إجمالي البلاغات" icon={TrendingUp} tint="#22D3EE" testid="kpi-completion" />
                  <KpiCard      label="البلاغات المفتوحة"     value={(kpis.openTickets ?? 0).toLocaleString('en-US')}
                    unit="بلاغ نشط" icon={FileWarning} tint="#F59E0B" onClick={() => nav('/alerts')} testid="kpi-open-tickets" />
                  <KpiCard      label="البلاغات المغلقة"      value={(kpis.completedTickets ?? 0).toLocaleString('en-US')}
                    unit="بلاغ مكتمل" icon={CheckCircle2} tint="#22C55E" onClick={() => nav('/alerts')} testid="kpi-closed-tickets" />
                  <KpiCard      label="أوامر العمل"           value={(modCounts.maint_work_orders ?? 0).toLocaleString('en-US')}
                    unit="أمر عمل مسجل" icon={ClipboardCheck} tint="#60A5FA"
                    onClick={() => nav('/imports/maintenance#work-orders')} testid="kpi-work-orders" />
                  <KpiCard      label="الموردون"              value={(modCounts.supply_suppliers ?? 0).toLocaleString('en-US')}
                    unit="مورد" icon={Building} tint="#8B5CF6"
                    onClick={() => nav('/supply/imports/supply_suppliers')} testid="kpi-suppliers" />
                  <KpiCard      label="التوريدات"             value={(supKpis.totalRows ?? 0).toLocaleString('en-US')}
                    unit={`مسلم: ${(supKpis.totalDelivered ?? 0).toLocaleString('en-US')}`} icon={Package} tint="#10B981"
                    onClick={() => nav('/supply')} testid="kpi-supplies" />
                  <KpiCard      label="الزيارات الميدانية"    value={(modCounts.maint_field_visits ?? 0).toLocaleString('en-US')}
                    unit="زيارة مسجلة" icon={MapPin} tint="#EC4899"
                    onClick={() => nav('/imports/maintenance#field-visits')} testid="kpi-field-visits" />
                </div>
              </section>

              {/* ── 2nd row: Trend + Pie + Status Summary ─────────────── */}
              <section className="fade-in">
                <SectionHead
                  eyebrow="تحليلات"
                  title="الاتجاه والتوزيع"
                  subtitle="اتجاه البلاغات · توزيع الفئات · ملخص الحالة"
                  testid="section-analytics"
                />
                <div className="grid grid-cols-12 gap-5 md:gap-6">
                  <div className="col-span-12 lg:col-span-5">
                    <TrendPanel data={data.trend} />
                  </div>
                  <div className="col-span-12 md:col-span-6 lg:col-span-4">
                    <DonutPanel data={data.ticketsByType} onCategoryClick={clickCategory} activeCategory={activeCategory} />
                  </div>
                  <div className="col-span-12 md:col-span-6 lg:col-span-3">
                    <StatusSummaryPanel kpis={kpis} />
                  </div>
                </div>
              </section>

              {/* ── 3rd row: Interactive Southern Region Map ─────────── */}
              <section className="fade-in">
                <SectionHead
                  eyebrow="جغرافياً"
                  title="خريطة المنطقة الجنوبية"
                  subtitle="اضغط على محافظة لعرض تفاصيلها · اضغط على أي مدرسة لفتح ملفها"
                  testid="section-map"
                  right={
                    <button onClick={() => nav('/schools')} className="btn btn-ghost">
                      <span>كل المدارس</span> <ArrowRight size={13} />
                    </button>
                  }
                />
                <div className="relative">
                  <MapPanel
                    data={data.map}
                    onSelectGovernorate={(name) => setDrawer({ open: true, key: 'governorate', value: name })}
                    onSelectSchool={(id) => openSchool(id)}
                  />
                </div>
              </section>

              {/* ── 4th row: Latest Tickets table ─────────────────────── */}
              <section className="fade-in">
                <SectionHead
                  eyebrow="بلاغات"
                  title="أحدث البلاغات"
                  subtitle="آخر بلاغات الصيانة المسجلة"
                  testid="section-latest-tickets"
                  right={<button onClick={() => nav('/alerts')} className="btn btn-ghost">عرض الكل <ArrowRight size={13} /></button>}
                />
                <LatestTicketsTable alerts={data.alerts} onSelectSchool={openSchool} />
              </section>

              {/* ── 5th row: Latest Supply table ──────────────────────── */}
              <section className="fade-in">
                <SectionHead
                  eyebrow="توريد"
                  title="أحدث التوريدات"
                  subtitle="آخر عمليات التوريد المسجلة"
                  testid="section-latest-supply"
                  right={<button onClick={() => nav('/supply')} className="btn btn-ghost">لوحة التوريد <ArrowRight size={13} /></button>}
                />
                <LatestSupplyTable />
              </section>
            </>
          )}

          <div className="text-center text-slate-500 text-[11px] pt-6">
            إصدار النظام 4.0.0 &nbsp; • &nbsp; شركة تطوير المباني © 2026
          </div>
        </div>

        <RegionDrawer
          open={drawer.open}
          filterKey={drawer.key}
          filterValue={drawer.value}
          onClose={() => setDrawer({ open: false, key: null, value: null })}
          allFilters={applied}
        />
      </main>
    </div>
  );
}
